import type {
  AccountAssignmentDto,
  AccountResponseDto,
  CrmAccountDetailsResultDto,
} from '../../features/accounts/models/account.models';
import type { CompanyDto } from '../../shared/companies/models/company.models';
import type { IndustryDto } from '../../shared/industries/models/industry.models';
import type { MomentumUser } from '../../shared/models/momentum-user.models';
import { MOCK_COMPANIES } from './companies-api-mock.handlers';

export const MOCK_ACCOUNT_OWNER: MomentumUser = {
  oId: '11111111-2222-3333-4444-555555555555',
  name: 'Jane Doe',
  email: 'jane@zuehlke.com',
};

export const MOCK_ACCOUNT_OTHER_OWNER: MomentumUser = {
  oId: '11111111-1111-1111-1111-111111111101',
  name: 'Alex Example',
  email: 'alex.example@zuehlke.com',
};

const MOCK_CRM_HELVETIA = 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa';
const MOCK_CRM_SBB = 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb';

const MOCK_COMPANY_ZCH: CompanyDto =
  MOCK_COMPANIES.find((row) => row.name === 'ZCH') ?? MOCK_COMPANIES[2];
const MOCK_COMPANY_ZDE: CompanyDto =
  MOCK_COMPANIES.find((row) => row.name === 'ZDE') ?? MOCK_COMPANIES[6];

const MOCK_INDUSTRY_FSI: IndustryDto = {
  id: '10000000-0000-4000-8000-000000000007',
  name: 'FSI - Banking',
};

const now = '2026-03-01T10:00:00.000Z';

function mockCrmAccountWebUrl(crmAccountId: string): string {
  return `https://mock.crm.example/main.aspx?pagetype=entityrecord&etn=account&id=${crmAccountId}`;
}

function emptyAccountTeamState(): Pick<
  AccountResponseDto,
  'currentAccountTeam' | 'formerAccountTeam' | 'formerOwners'
> {
  return {
    currentAccountTeam: [],
    formerAccountTeam: [],
    formerOwners: [],
  };
}

function activeTeamAssignment(user: MomentumUser): AccountAssignmentDto {
  return {
    id: `team-${globalThis.crypto?.randomUUID?.() ?? String(Date.now())}`,
    user,
    kind: 'AccountTeam',
    status: 'Active',
    addedAt: new Date().toISOString(),
    removedAt: null,
    roleLabel: null,
  };
}

export const MOCK_ACCOUNTS: AccountResponseDto[] = [
  {
    id: 'acc-0001-0001-0001-000000000001',
    crmAccountId: MOCK_CRM_HELVETIA,
    crmAccountWebUrl: mockCrmAccountWebUrl(MOCK_CRM_HELVETIA),
    name: 'Helvetia',
    company: MOCK_COMPANY_ZCH,
    industry: MOCK_INDUSTRY_FSI,
    allocatedLibraryName: 'ZCH',
    allocatedShardIndex: 1,
    createdBy: MOCK_ACCOUNT_OWNER,
    owner: MOCK_ACCOUNT_OWNER,
    createdAt: now,
    updatedAt: now,
    ...emptyAccountTeamState(),
    currentAccountTeam: [activeTeamAssignment(MOCK_ACCOUNT_OTHER_OWNER)],
  },
  {
    id: 'acc-0002-0002-0002-000000000002',
    crmAccountId: MOCK_CRM_SBB,
    crmAccountWebUrl: mockCrmAccountWebUrl(MOCK_CRM_SBB),
    name: 'SBB',
    company: MOCK_COMPANY_ZCH,
    industry: MOCK_INDUSTRY_FSI,
    allocatedLibraryName: 'ZCH',
    allocatedShardIndex: 1,
    createdBy: MOCK_ACCOUNT_OTHER_OWNER,
    owner: MOCK_ACCOUNT_OTHER_OWNER,
    createdAt: now,
    updatedAt: now,
    ...emptyAccountTeamState(),
  },
];

export const MOCK_CRM_DIRECTORY: readonly {
  crmAccountId: string;
  name: string;
  company: CompanyDto;
  industry: IndustryDto;
}[] = [
  {
    crmAccountId: MOCK_CRM_HELVETIA,
    name: 'Helvetia',
    company: MOCK_COMPANY_ZCH,
    industry: MOCK_INDUSTRY_FSI,
  },
  {
    crmAccountId: MOCK_CRM_SBB,
    name: 'SBB',
    company: MOCK_COMPANY_ZCH,
    industry: MOCK_INDUSTRY_FSI,
  },
  {
    crmAccountId: 'cccccccc-cccc-cccc-cccc-cccccccccccc',
    name: 'Deutsche Bahn',
    company: MOCK_COMPANY_ZDE,
    industry: MOCK_INDUSTRY_FSI,
  },
];

export function mockFindAccountById(accountId: string): AccountResponseDto | undefined {
  return MOCK_ACCOUNTS.find((row) => row.id === accountId);
}

export function mockFindAccountByCrmId(crmAccountId: string): AccountResponseDto | undefined {
  return MOCK_ACCOUNTS.find((row) => row.crmAccountId === crmAccountId);
}

export function mockSearchCrmDirectory(
  search: string,
  limit: number,
): {
  accountId: string;
  name: string;
  zuehlkeCompanyName: string;
  industryName: string;
}[] {
  const needle = search.trim().toLowerCase();
  if (needle.length < 3) {
    return [];
  }
  return MOCK_CRM_DIRECTORY.filter((row) => row.name.toLowerCase().includes(needle))
    .slice(0, limit)
    .map((row) => ({
      accountId: row.crmAccountId,
      name: row.name,
      zuehlkeCompanyName: row.company.name,
      industryName: row.industry.name,
    }));
}

export function mockCrmDetails(crmAccountId: string): CrmAccountDetailsResultDto | null {
  const row = MOCK_CRM_DIRECTORY.find((entry) => entry.crmAccountId === crmAccountId);
  if (!row) {
    return null;
  }
  return {
    accountId: row.crmAccountId,
    name: row.name,
    company: row.company,
    industry: row.industry,
  };
}

export function mockCreateAccount(
  crmAccountId: string,
  createdBy: MomentumUser,
): AccountResponseDto {
  const existing = mockFindAccountByCrmId(crmAccountId);
  if (existing) {
    return existing;
  }

  const crm = MOCK_CRM_DIRECTORY.find((entry) => entry.crmAccountId === crmAccountId);
  if (!crm) {
    throw new Error('CRM account not found');
  }

  const created: AccountResponseDto = {
    id: `acc-${globalThis.crypto?.randomUUID?.() ?? String(Date.now())}`,
    crmAccountId,
    crmAccountWebUrl: mockCrmAccountWebUrl(crmAccountId),
    name: crm.name,
    company: crm.company,
    industry: crm.industry,
    allocatedLibraryName: crm.company.name,
    allocatedShardIndex: 1,
    createdBy,
    owner: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...emptyAccountTeamState(),
  };
  MOCK_ACCOUNTS.push(created);
  return created;
}

function mockFindAccountIndex(accountId: string): number {
  return MOCK_ACCOUNTS.findIndex((row) => row.id === accountId);
}

export function mockInviteAccountTeamMember(
  accountId: string,
  member: MomentumUser,
): AccountResponseDto | null {
  const index = mockFindAccountIndex(accountId);
  if (index < 0 || !member.oId?.trim()) {
    return null;
  }

  const account = MOCK_ACCOUNTS[index];
  const normalized = member.oId.trim().toLowerCase();
  const ownerOId = account.owner?.oId?.trim().toLowerCase();
  if (ownerOId === normalized) {
    throw new Error('Owner already has account access.');
  }

  const current = account.currentAccountTeam ?? [];
  if (current.some((row) => row.user.oId?.trim().toLowerCase() === normalized)) {
    throw new Error('User already has account access.');
  }

  const updated: AccountResponseDto = {
    ...account,
    currentAccountTeam: [...current, activeTeamAssignment(member)],
    updatedAt: new Date().toISOString(),
  };
  MOCK_ACCOUNTS[index] = updated;
  return updated;
}

export function mockRevokeAccountTeamMember(
  accountId: string,
  userOId: string,
): AccountResponseDto | null {
  const index = mockFindAccountIndex(accountId);
  if (index < 0) {
    return null;
  }

  const account = MOCK_ACCOUNTS[index];
  const normalized = userOId.trim().toLowerCase();
  const current = account.currentAccountTeam ?? [];
  const match = current.find((row) => row.user.oId?.trim().toLowerCase() === normalized);
  if (!match) {
    return null;
  }

  const revoked: AccountAssignmentDto = {
    ...match,
    status: 'Revoked',
    removedAt: new Date().toISOString(),
  };

  const updated: AccountResponseDto = {
    ...account,
    currentAccountTeam: current.filter((row) => row.id !== match.id),
    formerAccountTeam: [...(account.formerAccountTeam ?? []), revoked],
    updatedAt: new Date().toISOString(),
  };
  MOCK_ACCOUNTS[index] = updated;
  return updated;
}

export function mockUpdateAccountOwner(
  accountId: string,
  owner: MomentumUser,
): AccountResponseDto | null {
  const index = mockFindAccountIndex(accountId);
  if (index < 0) {
    return null;
  }
  const updated: AccountResponseDto = {
    ...MOCK_ACCOUNTS[index],
    owner,
    updatedAt: new Date().toISOString(),
  };
  MOCK_ACCOUNTS[index] = updated;
  return updated;
}

export function mockFilterAccountsList(
  search: string,
  companyId: string | null,
  createdByOid: string | null,
): AccountResponseDto[] {
  let rows = [...MOCK_ACCOUNTS];
  const needle = search.trim().toLowerCase();
  if (needle) {
    rows = rows.filter((row) => row.name.toLowerCase().includes(needle));
  }
  if (companyId) {
    rows = rows.filter((row) => row.company?.id === companyId);
  }
  if (createdByOid) {
    const normalized = createdByOid.trim().toLowerCase();
    rows = rows.filter((row) => row.createdBy.oId?.trim().toLowerCase() === normalized);
  }
  return rows.sort((a, b) => a.name.localeCompare(b.name));
}
